import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollApi, employeesApi, exportApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  IndianRupee, 
  Download, 
  FileText,
  Calculator,
  Building2,
  Eye,
  Printer,
  Edit3,
  RefreshCw,
  Upload,
  Search,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  Users,
  FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { RADIANCE_LOGO_BASE64 } from '../constants/logo';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

// Use base64 encoded logo to avoid CORS issues with html2canvas
const LOGO_URL = "/karya-logo.png";

export default function PayrollPage() {
  const { user, isHRManager, canAccessPayroll } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(1); // Default to January
  const [selectedYear, setSelectedYear] = useState(2025); // Default to 2025
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(!isHRManager); // For employees, show all by default
  const [editForm, setEditForm] = useState({
    basic_salary: 0,
    hra: 0,
    conveyance: 0,
    medical: 0,
    special_allowance: 0
  });

  useEffect(() => {
    // For employees, always show all history by default
    if (!isHRManager) {
      setShowAllHistory(true);
    }
  }, [isHRManager]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear, selectedEmployee, showAllHistory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // For employees, fetch all payslips (no month/year filter) by default
      const params = {};
      
      if (isHRManager) {
        // HR Manager: always filter by month/year
        params.month = selectedMonth;
        params.year = selectedYear;
        if (selectedEmployee && selectedEmployee !== 'all') {
          params.employee_id = selectedEmployee;
        }
      } else {
        // Employee: show all payslips unless filtered
        if (!showAllHistory) {
          params.month = selectedMonth;
          params.year = selectedYear;
        }
      }

      const payslipsRes = await payrollApi.getAll(params);
      setPayslips(payslipsRes.data);

      if (isHRManager) {
        const empRes = await employeesApi.getAll();
        setEmployees(empRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch payroll:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayroll = async () => {
    if (!window.confirm(`Generate payroll for ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}?`)) {
      return;
    }

    setGenerating(true);
    try {
      const response = await payrollApi.generate(selectedMonth, selectedYear);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payroll');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegeneratePayroll = async () => {
    if (!window.confirm(`This will delete and regenerate ALL payslips for ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}. Continue?`)) {
      return;
    }

    setGenerating(true);
    try {
      const response = await payrollApi.regenerate(selectedMonth, selectedYear);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate payroll');
    } finally {
      setGenerating(false);
    }
  };

  const handleImportFromExcel = async () => {
    if (!window.confirm('Import payroll data from the uploaded Excel file? This will add missing payslips.')) {
      return;
    }

    setImporting(true);
    try {
      const response = await payrollApi.importFromExcel();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import payroll');
    } finally {
      setImporting(false);
    }
  };

  const handleViewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setShowPayslipModal(true);
  };

  const handleEditPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setEditForm({
      basic_salary: payslip.basic_salary || 0,
      hra: payslip.hra || 0,
      conveyance: payslip.conveyance || 0,
      medical: payslip.medical || 0,
      special_allowance: payslip.special_allowance || 0
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await payrollApi.update(selectedPayslip.id, editForm);
      toast.success('Payslip updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payslip');
    }
  };

  const handlePrintPayslip = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!selectedPayslip) return;

    const toastId = toast.loading('Generating PDF...');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let y = 20;

      const monthName = MONTHS.find(m => m.value === selectedPayslip.month)?.label || '';
      const formatAmt = (amt) => 'Rs. ' + (amt || 0).toLocaleString('en-IN');

      // === HEADER ===
      // Logo - using URL
      try {
        // Skip logo in PDF to avoid size issues, or use placeholder
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(45, 212, 191);
        pdf.text('K', margin + 10, y + 12);
        pdf.setDrawColor(45, 212, 191);
        pdf.roundedRect(margin, y, 25, 18, 3, 3, 'S');
      } catch (e) {
        console.log('Logo error:', e);
      }

      // Company name
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 136, 229);
      pdf.text('Your Company Name', margin + 35, y + 5);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Company Address, City, State - Pincode', margin + 35, y + 10);

      // PAYSLIP badge
      pdf.setFillColor(30, 136, 229);
      pdf.roundedRect(pageWidth - margin - 25, y, 25, 10, 2, 2, 'F');
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text('PAYSLIP', pageWidth - margin - 12.5, y + 6.5, { align: 'center' });

      y += 18;

      // Blue divider
      pdf.setDrawColor(30, 136, 229);
      pdf.setLineWidth(0.8);
      pdf.line(margin, y, pageWidth - margin, y);

      y += 8;

      // === EMPLOYEE INFO ===
      pdf.setFillColor(240, 245, 250);
      pdf.roundedRect(margin, y, contentWidth, 16, 2, 2, 'F');
      
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('EMPLOYEE NAME', margin + 5, y + 5);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(40, 40, 40);
      pdf.text(selectedPayslip.employee_name || 'N/A', margin + 5, y + 12);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('PAY PERIOD', pageWidth - margin - 5, y + 5, { align: 'right' });
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(40, 40, 40);
      pdf.text(monthName + ' ' + selectedPayslip.year, pageWidth - margin - 5, y + 12, { align: 'right' });

      y += 24;

      // === EARNINGS & DEDUCTIONS TABLE ===
      const colWidth = (contentWidth - 10) / 2;
      const leftX = margin;
      const rightX = margin + colWidth + 10;
      const rowH = 8;

      // Headers
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(34, 197, 94);
      pdf.text('Earnings', leftX, y);
      pdf.setTextColor(239, 68, 68);
      pdf.text('Deductions', rightX, y);

      y += 6;

      // Data
      const earnings = [
        ['Basic Salary', selectedPayslip.basic_salary],
        ['HRA', selectedPayslip.hra],
        ['Conveyance', selectedPayslip.conveyance],
        ['Medical Allowance', selectedPayslip.medical],
        ['Special Allowance', selectedPayslip.special_allowance]
      ];

      const deductions = [
        ['Provident Fund (PF)', selectedPayslip.pf_deduction],
        ['ESI', selectedPayslip.esi_deduction],
        ['Professional Tax', selectedPayslip.professional_tax],
        ['TDS', selectedPayslip.tds]
      ];

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      // Draw rows
      const maxRows = Math.max(earnings.length, deductions.length);
      for (let i = 0; i < maxRows; i++) {
        const rowY = y + (i * rowH);
        
        // Earnings row
        if (earnings[i]) {
          pdf.setTextColor(80, 80, 80);
          pdf.text(earnings[i][0], leftX, rowY + 5);
          pdf.setTextColor(40, 40, 40);
          pdf.text(formatAmt(earnings[i][1]), leftX + colWidth, rowY + 5, { align: 'right' });
          pdf.setDrawColor(220, 220, 220);
          pdf.line(leftX, rowY + 7, leftX + colWidth, rowY + 7);
        }
        
        // Deductions row
        if (deductions[i]) {
          pdf.setTextColor(80, 80, 80);
          pdf.text(deductions[i][0], rightX, rowY + 5);
          pdf.setTextColor(40, 40, 40);
          pdf.text(formatAmt(deductions[i][1]), rightX + colWidth, rowY + 5, { align: 'right' });
          pdf.setDrawColor(220, 220, 220);
          pdf.line(rightX, rowY + 7, rightX + colWidth, rowY + 7);
        }
      }

      y += (maxRows * rowH) + 8;

      // === TOTALS ROW ===
      // Gross Earnings
      pdf.setFillColor(220, 252, 231);
      pdf.roundedRect(leftX, y, colWidth, 12, 2, 2, 'F');
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(34, 197, 94);
      pdf.text('Gross Earnings', leftX + 4, y + 8);
      pdf.text(formatAmt(selectedPayslip.gross_earnings), leftX + colWidth - 4, y + 8, { align: 'right' });

      // Total Deductions
      pdf.setFillColor(254, 226, 226);
      pdf.roundedRect(rightX, y, colWidth, 12, 2, 2, 'F');
      pdf.setTextColor(239, 68, 68);
      pdf.text('Total Deductions', rightX + 4, y + 8);
      pdf.text(formatAmt(selectedPayslip.total_deductions), rightX + colWidth - 4, y + 8, { align: 'right' });

      y += 18;

      // === NET SALARY ===
      pdf.setFillColor(30, 136, 229);
      pdf.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F');
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text('Net Salary Payable', margin + 8, y + 12);
      pdf.setFontSize(16);
      pdf.text(formatAmt(selectedPayslip.net_salary), pageWidth - margin - 8, y + 12, { align: 'right' });

      y += 26;

      // === FOOTER ===
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, y, pageWidth - margin, y);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(130, 130, 130);
      pdf.text('This is a computer-generated payslip and does not require a signature.', pageWidth / 2, y + 5, { align: 'center' });
      pdf.text('Generated on ' + new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }), pageWidth / 2, y + 10, { align: 'center' });

      // Save
      const fileName = `Payslip_${selectedPayslip.employee_name.replace(/\s+/g, '_')}_${monthName}_${selectedPayslip.year}.pdf`;
      pdf.save(fileName);
      
      toast.dismiss(toastId);
      toast.success('Payslip downloaded!');
    } catch (error) {
      console.error('PDF error:', error);
      toast.dismiss(toastId);
      toast.error('Failed to generate PDF');
    }
  };

  const handleExport = async (format = 'excel') => {
    try {
      toast.loading('Generating export...');
      const response = await exportApi.payroll(selectedMonth, selectedYear, format);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`Payroll exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to export payroll');
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format for compact display
  const formatCompact = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getYearOptions = () => {
    return [2024, 2025, 2026, 2027];
  };

  const filteredPayslips = payslips.filter(p => 
    !searchTerm || 
    p.employee_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalGross = filteredPayslips.reduce((sum, p) => sum + (p.gross_earnings || 0), 0);
  const totalNet = filteredPayslips.reduce((sum, p) => sum + (p.net_salary || 0), 0);
  const totalDeductions = filteredPayslips.reduce((sum, p) => sum + (p.total_deductions || 0), 0);
  const avgSalary = filteredPayslips.length > 0 ? totalNet / filteredPayslips.length : 0;

  const getStatusBadge = (status) => {
    switch(status) {
      case 'imported':
        return <Badge className="bg-blue-100 text-blue-700">Imported</Badge>;
      case 'edited':
        return <Badge className="bg-amber-100 text-amber-700">Edited</Badge>;
      case 'generated':
        return <Badge className="bg-green-100 text-green-700">Generated</Badge>;
      default:
        return <Badge variant="secondary">{status || 'New'}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="page-title">Payroll Management</h1>
          <p className="page-description">Process salaries, view payslips, and manage compensation</p>
        </div>
        {isHRManager && (
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline"
              className="gap-2"
              onClick={() => handleExport('excel')}
              data-testid="export-excel-btn"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </Button>
            <Button 
              variant="outline"
              className="gap-2"
              onClick={handleImportFromExcel}
              disabled={importing}
              data-testid="import-payroll-btn"
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button 
              variant="outline"
              className="gap-2"
              onClick={handleRegeneratePayroll}
              disabled={generating}
              data-testid="regenerate-payroll-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Regenerate
            </Button>
            <Button 
              className="btn-primary gap-2"
              onClick={handleGeneratePayroll}
              disabled={generating}
              data-testid="generate-payroll-btn"
            >
              <Calculator className="h-4 w-4" />
              {generating ? 'Processing...' : 'Generate Payroll'}
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {isHRManager && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Gross</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalGross)}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Deductions</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDeductions)}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Net Payable</p>
                  <p className="text-2xl font-bold text-[#1E88E5]">{formatCurrency(totalNet)}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#1E88E5]/10 flex items-center justify-center">
                  <IndianRupee className="h-6 w-6 text-[#1E88E5]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Payslips</p>
                  <p className="text-2xl font-bold">{filteredPayslips.length}</p>
                  <p className="text-xs text-muted-foreground">Avg: {formatCurrency(avgSalary)}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex flex-wrap gap-3 items-center">
              {/* For employees: Show "All History" toggle */}
              {!isHRManager && (
                <div className="flex items-center gap-2 mr-2">
                  <Button
                    variant={showAllHistory ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowAllHistory(true)}
                    className={showAllHistory ? "btn-primary" : ""}
                    data-testid="show-all-history-btn"
                  >
                    All History
                  </Button>
                  <Button
                    variant={!showAllHistory ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowAllHistory(false)}
                    className={!showAllHistory ? "btn-primary" : ""}
                    data-testid="filter-by-month-btn"
                  >
                    By Month
                  </Button>
                </div>
              )}

              {/* Month/Year filters - only show for HR or when employee chooses "By Month" */}
              {(isHRManager || !showAllHistory) && (
                <>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-40" data-testid="month-filter">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(month => (
                        <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-28" data-testid="year-filter">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {getYearOptions().map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}

              {isHRManager && (
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-56" data-testid="employee-filter">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex-1 lg:max-w-xs">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-payslip"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payslips Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-[#1E88E5]" />
                {!isHRManager && showAllHistory 
                  ? "All Payslips History" 
                  : `Payslips - ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
                }
              </CardTitle>
              <CardDescription>
                {filteredPayslips.length} payslips {searchTerm && `matching "${searchTerm}"`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="responsive-table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {/* Show Month column for employees viewing all history */}
                  {!isHRManager && showAllHistory && (
                    <TableHead className="font-semibold w-[120px]">Pay Period</TableHead>
                  )}
                  <TableHead className="font-semibold min-w-[180px]">Employee</TableHead>
                  <TableHead className="text-right font-semibold w-[100px]">Basic</TableHead>
                  <TableHead className="text-right font-semibold w-[100px]">HRA</TableHead>
                  <TableHead className="text-right font-semibold w-[100px]">Allowances</TableHead>
                  <TableHead className="text-right font-semibold w-[110px]">Gross</TableHead>
                  <TableHead className="text-right font-semibold w-[100px]">Deductions</TableHead>
                  <TableHead className="text-right font-semibold w-[110px]">Net Salary</TableHead>
                  <TableHead className="text-center font-semibold w-[90px]">Status</TableHead>
                  <TableHead className="text-center font-semibold w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(showAllHistory && !isHRManager ? 10 : 9)].map((_, j) => (
                        <TableCell key={j}>
                          <div className="h-4 bg-muted rounded animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredPayslips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showAllHistory && !isHRManager ? 10 : 9} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <FileText className="h-12 w-12 text-muted-foreground/50" />
                        <div>
                          <p className="font-medium text-muted-foreground">No payslips found</p>
                          <p className="text-sm text-muted-foreground/75">
                            {isHRManager ? 'Click "Import Excel" to load payroll data or "Generate Payroll" to create new payslips.' : 'No payslips available.'}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayslips.map((payslip) => (
                    <TableRow key={payslip.id} className="hover:bg-muted/30 transition-colors">
                      {/* Show Month for employees viewing all history */}
                      {!isHRManager && showAllHistory && (
                        <TableCell className="font-medium text-[#1E88E5]">
                          {MONTHS.find(m => m.value === payslip.month)?.label?.slice(0, 3)} {payslip.year}
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{payslip.employee_name}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{formatCurrency(payslip.basic_salary)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{formatCurrency(payslip.hra)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {formatCurrency((payslip.conveyance || 0) + (payslip.medical || 0) + (payslip.special_allowance || 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-medium">{formatCurrency(payslip.gross_earnings)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-red-600">{formatCurrency(payslip.total_deductions)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-semibold text-green-600">{formatCurrency(payslip.net_salary)}</TableCell>
                      <TableCell className="text-center">{getStatusBadge(payslip.status)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewPayslip(payslip)}
                            data-testid={`view-payslip-${payslip.id}`}
                            className="h-8 w-8 p-0"
                            title="View payslip"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {isHRManager && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditPayslip(payslip)}
                              data-testid={`edit-payslip-${payslip.id}`}
                              className="h-8 w-8 p-0"
                              title="Edit payslip"
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Payslip Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="max-w-2xl print:max-w-none print:m-0 print:p-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle>Payslip Details</DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${MONTHS.find(m => m.value === selectedPayslip.month)?.label} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>

          {selectedPayslip && (
            <div id="payslip-content" className="payslip-container payslip-print-container space-y-4 border rounded-xl p-6 bg-white">
              {/* Company Header */}
              <div className="flex items-center justify-between border-b-2 border-[#1E88E5] pb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src={LOGO_URL} 
                    alt="Company Logo" 
                    className="h-16 w-16 object-contain"
                    crossOrigin="anonymous"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-[#1E88E5]">Your Company Name</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Company Address, City, State - Pincode
                    </p>
                    <p className="text-xs text-gray-400">
                      CIN: U45201TN2010PLC075567 | PAN: AAECR1234A
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <Badge className="bg-[#1E88E5] text-white text-sm px-5 py-1.5 font-semibold">PAYSLIP</Badge>
                  <p className="text-xs mt-2 font-medium text-gray-600">
                    {MONTHS.find(m => m.value === selectedPayslip.month)?.label} {selectedPayslip.year}
                  </p>
                </div>
              </div>

              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4 bg-[#F3F7FB] p-4 rounded-lg">
                <div>
                  <p className="text-gray-500 text-[10px] uppercase tracking-wide">Employee Name</p>
                  <p className="font-semibold text-base mt-0.5 text-gray-900">{selectedPayslip.employee_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wide">Pay Period</p>
                  <p className="font-semibold text-base mt-0.5 text-gray-900">
                    {MONTHS.find(m => m.value === selectedPayslip.month)?.label} {selectedPayslip.year}
                  </p>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid grid-cols-2 gap-6">
                {/* Earnings Column */}
                <div>
                  <h3 className="font-semibold text-[#22c55e] flex items-center gap-2 text-sm mb-3">
                    <TrendingUp className="h-4 w-4" />
                    Earnings
                  </h3>
                  <div className="space-y-0 text-sm">
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Basic Salary</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.basic_salary)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">HRA</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.hra)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Conveyance</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.conveyance)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Medical Allowance</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.medical)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Special Allowance</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.special_allowance)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Column */}
                <div>
                  <h3 className="font-semibold text-[#ef4444] flex items-center gap-2 text-sm mb-3">
                    <TrendingDown className="h-4 w-4" />
                    Deductions
                  </h3>
                  <div className="space-y-0 text-sm">
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Provident Fund (PF)</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.pf_deduction)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">ESI</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.esi_deduction)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">Professional Tax</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.professional_tax)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-dashed border-gray-200">
                      <span className="text-gray-600 text-xs">TDS</span>
                      <span className="tabular-nums font-medium text-gray-900 text-xs">{formatCurrency(selectedPayslip.tds)}</span>
                    </div>
                    <div className="py-1.5 border-b border-transparent">
                      <span className="text-xs">&nbsp;</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gross Earnings & Total Deductions - ALIGNED */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex justify-between items-center font-semibold text-[#22c55e] bg-[#dcfce7] p-3 rounded-lg text-sm">
                  <span>Gross Earnings</span>
                  <span className="tabular-nums">{formatCurrency(selectedPayslip.gross_earnings)}</span>
                </div>
                <div className="flex justify-between items-center font-semibold text-[#ef4444] bg-[#fee2e2] p-3 rounded-lg text-sm">
                  <span>Total Deductions</span>
                  <span className="tabular-nums">{formatCurrency(selectedPayslip.total_deductions)}</span>
                </div>
              </div>

              {/* Net Salary */}
              <div className="flex justify-between items-center p-4 rounded-xl bg-[#1E88E5] text-white">
                <span className="text-base font-semibold">Net Salary Payable</span>
                <span className="text-2xl font-bold tabular-nums">
                  {formatCurrency(selectedPayslip.net_salary)}
                </span>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                <p className="font-medium">This is a computer-generated payslip and does not require a signature.</p>
                <p className="mt-1">Generated on {new Date(selectedPayslip.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          )}

          <DialogFooter className="no-print flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>Close</Button>
            <Button variant="outline" className="gap-2" onClick={handlePrintPayslip}>
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button className="btn-primary gap-2" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
              <FileDown className="h-4 w-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payslip Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-[#1E88E5]" />
              Edit Payslip
            </DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${selectedPayslip.employee_name} - ${MONTHS.find(m => m.value === selectedPayslip.month)?.label} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="basic">Basic Salary</Label>
              <Input
                id="basic"
                type="number"
                value={editForm.basic_salary}
                onChange={(e) => setEditForm({ ...editForm, basic_salary: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hra">HRA</Label>
              <Input
                id="hra"
                type="number"
                value={editForm.hra}
                onChange={(e) => setEditForm({ ...editForm, hra: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conveyance">Conveyance</Label>
              <Input
                id="conveyance"
                type="number"
                value={editForm.conveyance}
                onChange={(e) => setEditForm({ ...editForm, conveyance: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="medical">Medical Allowance</Label>
              <Input
                id="medical"
                type="number"
                value={editForm.medical}
                onChange={(e) => setEditForm({ ...editForm, medical: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="special">Special Allowance</Label>
              <Input
                id="special"
                type="number"
                value={editForm.special_allowance}
                onChange={(e) => setEditForm({ ...editForm, special_allowance: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
            </div>

            {/* Preview calculation */}
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Preview (Gross)</p>
              <p className="text-xl font-bold font-mono text-[#1E88E5]">
                {formatCurrency(
                  editForm.basic_salary + editForm.hra + editForm.conveyance + 
                  editForm.medical + editForm.special_allowance
                )}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
